import React, { useState } from 'react'
import {
  Button,
  Intent,
  PopoverPosition,
  Classes,
  Popover,
} from '@blueprintjs/core'
import { Smiley } from 'phosphor-react'

import EmojiPicker from '@client/components/EmojiPicker'
import {
  TITLE_MAX_LENGTH,
  countCharacters,
  fitsLimit,
  limitEditWithCursor,
  restoreCursor,
} from '@client/utils/textLimit'

import './styles.scss'

export const SubjectField = ({
  name = 'subject',
  value,
  limitMax,
  limitMin,
  count,
  onChange,
  formatMessage,
}) => {
  const node = React.useRef()
  const addon = React.useRef()
  const picker = React.useRef()
  const [open, setOpen] = React.useState(false)
  // The API rejects more than 255 characters (FE-1491). Unlike `limitMax`,
  // which is only a recommendation, this is a hard limit: an edit that goes
  // past it is cut, and an emoji that doesn't fit isn't inserted.
  const [editCut, setEditCut] = React.useState(false)
  const limitReached = editCut || countCharacters(value) >= TITLE_MAX_LENGTH

  const handleChange = React.useCallback(
    (e) => {
      const { value: next, cursor } = limitEditWithCursor(value, e.target.value)
      setEditCut(next !== e.target.value)
      onChange({ target: { value: next } })
      restoreCursor(e.target, cursor)
    },
    [onChange, value]
  )

  const handleEmojiSelect = React.useCallback(
    (emoji) => {
      const next = value + emoji.native
      const fits = fitsLimit(next)
      setEditCut(!fits)
      if (fits) {
        onChange({ target: { value: next } })
      }
      setOpen(false)
    },
    [onChange, setOpen, value]
  )

  const handleClickOutside = React.useCallback(
    (e) => {
      if (
        (node.current && node.current.contains(e.target)) ||
        (picker.current && picker.current.contains(e.target))
      ) {
        // inside click
        return
      }
      // outside click
      setOpen(false)
    },
    [node, setOpen]
  )

  React.useEffect(() => {
    if (open) {
      document.addEventListener('mousedown', handleClickOutside)
    } else {
      document.removeEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [open, handleClickOutside])

  return (
    <div className="subject-field mt-4" ref={node}>
      <label className="flex items-baseline justify-between" htmlFor={name}>
        <span className="font-medium">{formatMessage(`header.${name}`)}</span>
        <span
          title={formatMessage(`header.modal.${name}.tooltip`, {
            limit: limitMax,
          })}
          className={`text-xs ${
            count > limitMax || count < limitMin
              ? 'text-red-600'
              : 'text-emerald-600'
          }`}
        >
          {count} / {limitMax}
        </span>
      </label>
      <div className="bp6-control-group">
        <div className="bp6-input-group bp6-fill">
          <input
            className="mt-0.5 block w-full max-w-sm rounded-md border-gray-300 px-2.5 py-1.5 pr-10 text-sm transition placeholder:text-gray-400 invalid:border-red-500 hover:border-gray-400 focus:border-blue-500 focus:ring-0"
            type="text"
            value={value}
            onChange={handleChange}
            placeholder={formatMessage(`header.modal.${name}.placeholder`)}
            name={name}
            id={name}
            required={limitMin > 0}
            minLength={limitMin}
          />
          <Popover
            interactionKind="click"
            popoverClassName="subject-field"
            position={PopoverPosition.BOTTOM_RIGHT}
            popoverRef={picker}
            content={
              <div>
                <EmojiPicker
                  showPreview={false}
                  showSkinTones={false}
                  onEmojiSelect={handleEmojiSelect}
                />
              </div>
            }
            isOpen={open}
          >
            <span
              ref={addon}
              className="bp6-icon gan-subject__emoji-icon mt-0.5 flex h-[34px] w-[34px] items-center justify-center rounded-r-md border border-gray-300 hover:border-gray-400 focus:border-blue-500"
              onClick={() => setOpen((prevState) => !prevState)}
              data-testid="emoji-button"
            >
              <Smiley className="min-w-[15px]" size={15} />
            </span>
          </Popover>
        </div>
      </div>
      <div className="mt-1 text-xs text-gray-500">
        {count > 0 && count > limitMax ? (
          <span data-testid="field-notice">
            {formatMessage(`header.modal.${name}.help.error`, {
              limit: limitMax,
            })}
          </span>
        ) : null}
        {count > 0 && count <= limitMax ? (
          <span data-testid="field-success">
            {formatMessage(`header.modal.${name}.help.success`, {
              limit: limitMax,
            })}
          </span>
        ) : null}
        {count < limitMin ? (
          <span className="text-red-600" data-testid="field-error">
            {formatMessage(`header.modal.${name}.help.empty`)}
          </span>
        ) : null}
        {limitReached ? (
          <span className="block text-red-600" data-testid="field-limit">
            {formatMessage('header.limitReached', { limit: TITLE_MAX_LENGTH })}
          </span>
        ) : null}
      </div>
    </div>
  )
}

export function InboxPreviewCard({ editor, setIsOpen, formatMessage }) {
  let [title, setTitle] = useState(editor.subject || '')
  let [titleCount, setTitleCount] = useState(title.length)
  let titleLimitMax = 50
  let titleLimitMin = 1

  let [description, setDescription] = useState(editor.pre_header || '')
  let [descriptionCount, setDescriptionCount] = useState(description.length)
  let descriptionLimitMax = 50
  let descriptionLimitMin = 0
  let handleTitleChange = (e) => {
    setTitle(e.target.value)
    setTitleCount(e.target.value.length)
  }

  let handleDescriptionChange = (e) => {
    setDescription(e.target.value)
    setDescriptionCount(e.target.value.length)
  }

  const handleSave = (event) => {
    event.preventDefault()
    event.stopPropagation()
    if (!editor.isSubjectValid(title)) {
      return
    }
    editor.setSubject(title)
    editor.setPreHeader(description)
    editor.save()
    setIsOpen(false)
  }

  return (
    <div className="gan-tw relative mx-auto grid bg-white md:grid-cols-2">
      <div className="mx-auto max-w-md px-10 py-8">
        <h3 className="text-base font-semibold">
          {formatMessage('header.modal.title')}
        </h3>
        <p className="mt-1">{formatMessage('header.modal.description')}</p>
        <SubjectField
          name="subject"
          value={title}
          count={titleCount}
          limitMax={titleLimitMax}
          limitMin={titleLimitMin}
          onChange={handleTitleChange}
          formatMessage={formatMessage}
        />
        <SubjectField
          name="preheader"
          value={description}
          count={descriptionCount}
          limitMax={descriptionLimitMax}
          limitMin={descriptionLimitMin}
          onChange={handleDescriptionChange}
          formatMessage={formatMessage}
        />
        <div className="mt-7 flex items-center justify-end gap-4">
          <div className="flex items-center gap-4">
            <Button
              type="button"
              onClick={() => setIsOpen(false)}
              data-testid="cancel-button"
            >
              {formatMessage('common.cancel')}
            </Button>
            <Button
              type="button"
              intent={Intent.SUCCESS}
              onClick={handleSave}
              disabled={!editor.isSubjectValid(title)}
              data-testid="save-button"
            >
              {formatMessage('common.save')}
            </Button>
          </div>
        </div>
      </div>

      {/* Share preview */}
      <div className="relative flex flex-col justify-center bg-gray-200 px-9 transition-colors duration-500">
        <div className="divide-y overflow-hidden rounded-lg bg-white shadow-md ring-1 ring-black ring-opacity-5">
          <div className="grid gap-2 bg-gray-50 px-4 py-4">
            <div className="h-4 w-2/5 rounded-md bg-gray-300"></div>
            <div className="h-3 w-4/5 rounded-md bg-gray-200"></div>
          </div>
          <div className="flex gap-2 px-4 py-3">
            <div className="mt-[5px] h-2.5 w-2.5 rounded-full bg-blue-500"></div>
            <div className="max-w-full flex-1">
              <div className="text-[14px] font-semibold text-gray-900">
                {formatMessage('header.modal.preview.sender')}
              </div>
              <div className="line-clamp-1 text-gray-900">
                {title.length
                  ? title
                  : formatMessage('header.modal.preview.subject')}
              </div>
              <div className="line-clamp-1 text-gray-500">
                {description.length
                  ? description
                  : formatMessage('header.modal.preview.preheader')}
              </div>
            </div>
          </div>
          <div className="grid gap-2 bg-gray-50 px-4 py-4">
            <div className="h-4 w-2/5 rounded-md bg-gray-300"></div>
            <div className="h-3 w-4/5 rounded-md bg-gray-200"></div>
          </div>
        </div>
      </div>
    </div>
  )
}
